// src/components/Layout.jsx - Kyber Security Branded Layout
import React from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Layout = () => {
  const { user, logout, canAccess } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <header style={{
        backgroundColor: 'white',
        borderBottom: '3px solid #6B3FA0',
        boxShadow: '0 2px 8px rgba(107, 63, 160, 0.1)'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '20px 30px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          {/* Logo and Title */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <img 
              src="/images/342px_x_216px-01.png" 
              alt="Kyber Security" 
              style={{ height: '50px', width: 'auto' }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div>
              <h1 style={{ 
                margin: 0, 
                fontSize: '24px', 
                color: '#6B3FA0',
                fontWeight: '700'
              }}>
                Assessment Portal
              </h1>
              <p style={{ 
                margin: 0, 
                fontSize: '14px', 
                color: '#7E4DB3',
                fontWeight: '400'
              }}>
                Security Compliance Management
              </p>
            </div>
          </div>

          {/* User Info and Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ 
                fontSize: '14px', 
                color: '#4A2B70', 
                fontWeight: '600' 
              }}>
                {user?.full_name || user?.username}
              </div>
              <div style={{ 
                fontSize: '12px', 
                color: '#7E4DB3',
                textTransform: 'capitalize'
              }}>
                {user?.role || 'User'}
              </div>
            </div>
            <a
              href="/api/docs/user-guide"
              download
              title="Download User Guide"
              style={{
                padding: '10px 16px',
                backgroundColor: 'transparent',
                color: '#6B3FA0',
                border: '2px solid #6B3FA0',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#6B3FA0';
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#6B3FA0';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              User Guide
            </a>
            <button
              onClick={handleLogout}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6B3FA0',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(107, 63, 160, 0.2)'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#5A2F8A';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 8px rgba(107, 63, 160, 0.3)';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = '#6B3FA0';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 4px rgba(107, 63, 160, 0.2)';
              }}
            >
              Logout
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav style={{
          backgroundColor: '#F3EEFA',
          borderTop: '1px solid #E5D5F5'
        }}>
          <div style={{
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '0 30px',
            display: 'flex',
            gap: '5px'
          }}>
            <NavLink to="/" label="Dashboard" isActive={isActive('/')} exact />
            <NavLink to="/clients" label="Clients" isActive={isActive('/clients')} />
            {canAccess('assessments') && (
              <NavLink to="/assessments" label="Assessments" isActive={isActive('/assessments')} />
            )}
            {canAccess('vulnerability-scans') && (
              <NavLink to="/vulnerability-scans" label="Vulnerability Scans" isActive={isActive('/vulnerability-scans')} />
            )}
            {canAccess('penetration-tests') && (
              <NavLink to="/penetration-tests" label="Penetration Tests" isActive={isActive('/penetration-tests')} />
            )}
            {canAccess('ad-audits') && (
              <NavLink to="/active-directory-audits" label="AD Audits" isActive={isActive('/active-directory-audits')} />
            )}
            {canAccess('value-of-risk') && (
              <NavLink to="/vor/assessments" label="Value of Risk" isActive={isActive('/vor')} />
            )}
            {canAccess('nar') && (
              <NavLink to="/nar/assessments" label="NAR" isActive={isActive('/nar')} />
            )}
            <NavLink to="/dark-web" label="Dark Web" isActive={isActive('/dark-web')} />
            {canAccess('ai-governance') && (
              <NavLink to="/ai-gov" label="AI Governance" isActive={isActive('/ai-gov')} />
            )}
            {user?.role === 'admin' && (
              <NavLink to="/admin" label="Admin" isActive={isActive('/admin')} />
            )}
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main style={{ 
        flex: 1, 
        backgroundColor: '#F8F9FA',
        padding: '30px'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          <Outlet />
        </div>
      </main>

      {/* Footer */}
      <footer style={{
        backgroundColor: '#4A2B70',
        color: 'white',
        padding: '20px 30px',
        boxShadow: '0 -2px 8px rgba(74, 43, 112, 0.1)'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <p style={{ margin: 0, fontSize: '14px' }}>
            © {new Date().getFullYear()} Kyber Security. All rights reserved.
          </p>
          <div style={{ display: 'flex', gap: '20px', fontSize: '14px' }}>
            <a 
              href="https://www.kybersecure.com" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#F3EEFA', textDecoration: 'none' }}
            >
              www.kybersecure.com
            </a>
            <span style={{ color: '#7E4DB3' }}>|</span>
            <span>Support: support@kybersecure.com</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Navigation Link Component
const NavLink = ({ to, label, isActive, exact }) => {
  const baseStyle = {
    padding: '12px 20px',
    color: isActive ? '#4A2B70' : '#7E4DB3',
    textDecoration: 'none',
    fontWeight: isActive ? '600' : '500',
    fontSize: '15px',
    borderBottom: isActive ? '3px solid #6B3FA0' : '3px solid transparent',
    transition: 'all 0.2s ease',
    display: 'inline-block'
  };

  return (
    <Link 
      to={to} 
      style={baseStyle}
      onMouseOver={(e) => {
        if (!isActive) {
          e.target.style.color = '#6B3FA0';
          e.target.style.backgroundColor = 'rgba(107, 63, 160, 0.05)';
        }
      }}
      onMouseOut={(e) => {
        if (!isActive) {
          e.target.style.color = '#7E4DB3';
          e.target.style.backgroundColor = 'transparent';
        }
      }}
    >
      {label}
    </Link>
  );
};

export default Layout;
